/**
 * `createApiTracersSource` — the `pipeline-mgmt-api` tracers port over the shared
 * `ApiClient`.
 *
 * Tracers are "follow the money" flow-tracking definitions; each run snapshots
 * the discovered `InteractionGraph` (which `@authe/tx-graph` renders). The
 * backend speaks snake_case (`max_hops`, `min_value_raw`, seeds as
 * `{chain_id, value}`), so this adapter maps both directions and exposes a
 * camelCase domain to the feature page. Canonical `/tracers/*` paths are mapped
 * to `private/pipeline-mgmt-api` by `RESOURCE_REWRITES`.
 */
import { type ApiClient, unwrapData } from "@authe/fe-commons/api";

export type FlowDirection = "outbound" | "inbound" | "both";
export type TracerStatus = "active" | "paused" | "archived";
export type TracerScope = "user" | "team" | "tenant" | "org" | "customer" | "system";

export interface TracerSeed {
  chainId: string;
  value: string;
}

export interface TracerSummary {
  id: string;
  name: string;
  description?: string | null;
  scope: TracerScope;
  status: TracerStatus;
  direction: FlowDirection;
  maxHops: number;
  seeds: TracerSeed[];
  chainIds: string[];
  tokenFilters: string[];
  minValueRaw?: string | null;
  followBridges: boolean;
  autoRun: boolean;
  lastRunId?: string | null;
  lastRunAt?: number | null;
  runCount: number;
  /** Teams this tracer is shared with (backend ABAC `visible_to_team_ids`). */
  visibleToTeamIds: string[];
}

/** A node/edge graph snapshot shaped like the backend `InteractionGraph`. */
export interface TracerRunGraph {
  nodes: Record<string, any>[];
  edges: Record<string, any>[];
  node_count: number;
  edge_count: number;
  chain_ids: string[];
  multi_chain: boolean;
}

export interface TracerRunSummary {
  id: string;
  status: "running" | "ok" | "partial" | "failed";
  hopsWalked: number;
  nodeCount: number;
  edgeCount: number;
  totalValueRaw: string;
  terminalNodeCount: number;
  startedAt?: number | null;
  finishedAt?: number | null;
  durationMs?: number | null;
  error?: string | null;
  createdAt?: number | null;
  graph: TracerRunGraph;
}

export interface CreateTracerInput {
  name: string;
  description?: string | null;
  direction: FlowDirection;
  maxHops: number;
  seeds: TracerSeed[];
  chainIds?: string[];
  tokenFilters?: string[];
  minValueRaw?: string | null;
  followBridges?: boolean;
  autoRun?: boolean;
  scope?: TracerScope;
  /** Teams to share the tracer with (backend ABAC `visible_to_team_ids`). */
  visibleToTeamIds?: string[];
}

export interface TracersSource {
  list(): Promise<TracerSummary[]>;
  get(id: string): Promise<TracerSummary>;
  listRuns(id: string): Promise<TracerRunSummary[]>;
  create(input: CreateTracerInput): Promise<TracerSummary>;
  update(id: string, patch: Partial<CreateTracerInput> & { status?: TracerStatus }): Promise<TracerSummary>;
  remove(id: string): Promise<void>;
  runNow(id: string): Promise<{ queued: boolean; tracerId: string }>;
}

function seedFromWire(raw: Record<string, any>): TracerSeed {
  return { chainId: String(raw.chain_id ?? raw.chainId ?? ""), value: String(raw.value ?? "") };
}

function tracerFromWire(raw: Record<string, any>): TracerSummary {
  return {
    id: raw.id,
    name: raw.name ?? "",
    description: raw.description ?? null,
    scope: (raw.scope ?? "user") as TracerScope,
    status: (raw.status ?? "active") as TracerStatus,
    direction: (raw.direction ?? "outbound") as FlowDirection,
    maxHops: Number(raw.max_hops ?? raw.maxHops ?? 3),
    seeds: ((raw.seeds ?? []) as Record<string, any>[]).map(seedFromWire),
    chainIds: (raw.chain_ids ?? raw.chainIds ?? []) as string[],
    tokenFilters: (raw.token_filters ?? raw.tokenFilters ?? []) as string[],
    minValueRaw: raw.min_value_raw ?? raw.minValueRaw ?? null,
    followBridges: Boolean(raw.follow_bridges ?? raw.followBridges ?? true),
    autoRun: Boolean(raw.auto_run ?? raw.autoRun ?? true),
    lastRunId: raw.last_run_id ?? raw.lastRunId ?? null,
    lastRunAt: raw.last_run_at ?? raw.lastRunAt ?? null,
    runCount: Number(raw.run_count ?? raw.runCount ?? 0),
    visibleToTeamIds: (raw.visible_to_team_ids ?? raw.visibleToTeamIds ?? []) as string[],
  };
}

function tracerToWire(input: Partial<CreateTracerInput> & { status?: TracerStatus }): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (input.name !== undefined) body.name = input.name;
  if (input.description !== undefined) body.description = input.description;
  if (input.direction !== undefined) body.direction = input.direction;
  if (input.maxHops !== undefined) body.max_hops = input.maxHops;
  if (input.seeds !== undefined) {
    body.seeds = input.seeds.map((s) => ({ chain_id: s.chainId, value: s.value }));
  }
  if (input.chainIds !== undefined) body.chain_ids = input.chainIds;
  if (input.tokenFilters !== undefined) body.token_filters = input.tokenFilters;
  if (input.minValueRaw !== undefined) body.min_value_raw = input.minValueRaw;
  if (input.followBridges !== undefined) body.follow_bridges = input.followBridges;
  if (input.autoRun !== undefined) body.auto_run = input.autoRun;
  if (input.scope !== undefined) body.scope = input.scope;
  if (input.status !== undefined) body.status = input.status;
  if (input.visibleToTeamIds !== undefined) body.visible_to_team_ids = input.visibleToTeamIds;
  return body;
}

function runFromWire(raw: Record<string, any>): TracerRunSummary {
  const nodes = (raw.nodes ?? []) as Record<string, any>[];
  const edges = (raw.edges ?? []) as Record<string, any>[];
  return {
    id: raw.id,
    status: (raw.status ?? "ok") as TracerRunSummary["status"],
    hopsWalked: Number(raw.hops_walked ?? 0),
    nodeCount: Number(raw.node_count ?? nodes.length),
    edgeCount: Number(raw.edge_count ?? edges.length),
    totalValueRaw: String(raw.total_value_raw ?? "0"),
    terminalNodeCount: Number(raw.terminal_node_count ?? 0),
    startedAt: raw.started_at ?? null,
    finishedAt: raw.finished_at ?? null,
    durationMs: raw.duration_ms ?? null,
    error: raw.error ?? null,
    createdAt: raw.created_at ?? null,
    graph: {
      nodes,
      edges,
      node_count: Number(raw.node_count ?? nodes.length),
      edge_count: Number(raw.edge_count ?? edges.length),
      chain_ids: (raw.chain_ids ?? []) as string[],
      multi_chain: Boolean(raw.multi_chain ?? false),
    },
  };
}

function unwrapItems(data: any): Record<string, any>[] {
  return Array.isArray(data) ? data : (data?.items ?? []);
}

export function createApiTracersSource(client: ApiClient): TracersSource {
  const tracerPath = (id: string) => `/tracers/${encodeURIComponent(id)}`;

  return {
    async list(): Promise<TracerSummary[]> {
      const res = await client.get<Record<string, any>>("/tracers");
      return unwrapItems(unwrapData(res)).map(tracerFromWire);
    },
    async get(id: string): Promise<TracerSummary> {
      return tracerFromWire(unwrapData(await client.get<Record<string, any>>(tracerPath(id))));
    },
    async listRuns(id: string): Promise<TracerRunSummary[]> {
      const res = await client.get<Record<string, any>>(`${tracerPath(id)}/runs`);
      return unwrapItems(unwrapData(res)).map(runFromWire);
    },
    async create(input: CreateTracerInput): Promise<TracerSummary> {
      return tracerFromWire(unwrapData(await client.post<Record<string, any>>("/tracers", tracerToWire(input))));
    },
    async update(id, patch): Promise<TracerSummary> {
      return tracerFromWire(
        unwrapData(await client.patch<Record<string, any>>(tracerPath(id), tracerToWire(patch))),
      );
    },
    async remove(id: string): Promise<void> {
      unwrapData(await client.delete<void>(tracerPath(id)));
    },
    async runNow(id: string): Promise<{ queued: boolean; tracerId: string }> {
      const raw = unwrapData(await client.post<Record<string, any>>(`${tracerPath(id)}/run`, {}));
      return { queued: Boolean(raw?.queued), tracerId: raw?.tracer_id ?? id };
    },
  };
}
